// ============================================================================
// Sub Replacement — AM's list of requests. Default scope is "mine" (their own
// submissions); has_full_am_authority roles can flip to "all" to see every
// open request. Approval queue is a separate screen — this page is the
// origination + audit view, not the approval UI.
// ============================================================================

import { startsOnAllSubReplacements } from '../../shared/role-gates';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
} from '../../shared/components';
import { ExplainerButton } from '../../shared/explainer';
import {
  Skeleton,
  UrgencyBadge,
} from '../../shared/components';
import type { SubReplacementRequestRow, SubReplacementStatus } from '../../shared/types';
import { useSubReplacementRequests } from './hooks/use-sub-replacement-data';
import { SubChangeReasonTag, useSubChangeReasons } from './sub-change-reasons';

function isHighAuthority(role: string | null | undefined): boolean {
  // 2026-05-28: 'hr' and 'finance' added. HR needs sub replacement visibility
  // (these are employment events Antonela tracks); finance needs it for AP
  // context on partner payments. Both default to scope='all' with toggle.
  return startsOnAllSubReplacements(role);
}

const STATUS_CLASSES: Record<SubReplacementStatus, string> = {
  Pending: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Approved: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  Rejected: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
  Cancelled: 'bg-tcs-border text-tcs-text-secondary',
  Matching: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Awaiting_Onboarding: 'bg-indigo-100 text-indigo-900',
  Cutover_Pending: 'bg-violet-100 text-violet-900',
  Completed: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
};

function StatusChip({ status }: { status: SubReplacementStatus }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center rounded-md px-2 py-[2px] text-[11px] font-semibold uppercase tracking-wide ${
        STATUS_CLASSES[status] ?? 'bg-tcs-surface text-tcs-text-secondary'
      }`}
    >
      {status.replace('_', ' ')}
    </span>
  );
}

export default function SubReplacementList() {
  const navigate = useNavigate();
  const { employee, profile } = useAuth();
  const canSeeAll = isHighAuthority(profile?.role);
  const [scope, setScope] = useState<'mine' | 'all'>(canSeeAll ? 'all' : 'mine');

  const { data, isLoading, error, refetch, lastUpdated } =
    useSubReplacementRequests(
      scope === 'mine' && employee?.employee_id
        ? { requestedBy: employee.employee_id }
        : {},
    );

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, other: 0 };
    for (const row of data) {
      if (row.status === 'Pending') c.pending += 1;
      else if (row.status === 'Approved') c.approved += 1;
      else if (row.status === 'Rejected') c.rejected += 1;
      else c.other += 1;
    }
    return c;
  }, [data]);

  return (
    <div className="mx-auto max-w-5xl p-4 pb-28 sm:p-6">
      <PageHeader
        title="Sub Replacement"
        meta={
          isLoading
            ? 'Loading…'
            : `${counts.pending} pending · ${counts.approved} approved · ${counts.rejected} rejected${
                counts.other ? ` · ${counts.other} other` : ''
              }`
        }
        actions={
          <>
            <ExplainerButton surfaceKey="board:sub-replacement" />
            {canSeeAll && (
              <div className="flex items-center gap-1 rounded-md border border-tcs-border bg-tcs-bg p-0.5 text-sm">
                <button
                  className={`rounded px-3 py-1 ${
                    scope === 'mine'
                      ? 'bg-tcs-surface font-medium'
                      : 'text-tcs-text-secondary'
                  }`}
                  onClick={() => setScope('mine')}
                  type="button"
                >
                  Mine
                </button>
                <button
                  className={`rounded px-3 py-1 ${
                    scope === 'all'
                      ? 'bg-tcs-surface font-medium'
                      : 'text-tcs-text-secondary'
                  }`}
                  onClick={() => setScope('all')}
                  type="button"
                >
                  All
                </button>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              icon={<RefreshCw className="h-4 w-4" />}
              onClick={refetch}
              aria-label="Refresh"
            />
            {canSeeAll && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate('/sub-replacement/approval-queue')}
              >
                Approval queue
              </Button>
            )}
            <Button
              size="sm"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate('/sub-replacement/new')}
            >
              New
            </Button>
          </>
        }
      />

      {lastUpdated && (
        <div className="mt-2 text-[11px] text-tcs-text-muted">
          Updated {lastUpdated.toLocaleTimeString()}
        </div>
      )}

      {error && (
        <div className="mt-4">
          <ErrorState title="Failed to load" body={error} onRetry={refetch} />
        </div>
      )}

      {!error && isLoading && (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-20 rounded-md" />
          <Skeleton className="h-20 rounded-md" />
          <Skeleton className="h-20 rounded-md" />
        </div>
      )}

      {!error && !isLoading && data.length === 0 && (
        <div className="mt-10">
          <EmptyState
            title="No requests yet"
            body={
              scope === 'mine'
                ? "You haven't opened a Sub Replacement request yet."
                : 'No Sub Replacement requests in the system.'
            }
            cta={
              <Button
                icon={<Plus className="h-4 w-4" />}
                onClick={() => navigate('/sub-replacement/new')}
              >
                New request
              </Button>
            }
          />
        </div>
      )}

      {!error && !isLoading && data.length > 0 && (
        <ul className="mt-6 space-y-2">
          {data.map((row) => (
            <RequestRow key={row.request_id} row={row} />
          ))}
        </ul>
      )}
    </div>
  );
}

function RequestRow({ row }: { row: SubReplacementRequestRow }) {
  const { reasons } = useSubChangeReasons();
  const facilityLabel = row.facilities?.facility_name ?? row.facility_id;
  const accountLabel = row.accounts?.account_name ?? row.account_id;
  return (
    <li>
      <Link
        to={`/sub-replacement/${row.request_id}`}
        className="block rounded-md border border-tcs-border bg-tcs-bg px-3 py-3 transition hover:border-tcs-text/40"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-medium text-tcs-text">
              <span className="font-mono text-xs text-tcs-text-muted">
                {row.request_id}
              </span>
              <span className="truncate">{facilityLabel}</span>
            </div>
            <div className="mt-1 truncate text-xs text-tcs-text-secondary">
              {accountLabel}
              {row.outgoing_assignment?.role_title
                ? ` · ${row.outgoing_assignment.role_title}`
                : ''}
            </div>
            {row.reason_code && (
              <div className="mt-1.5">
                <SubChangeReasonTag code={row.reason_code} reasons={reasons} />
              </div>
            )}
            {row.reason && (
              <div className="mt-1 line-clamp-2 text-xs text-tcs-text-muted">
                {row.reason}
              </div>
            )}
            {row.status === 'Rejected' && row.rejection_reason && (
              <div className="mt-1 text-xs text-st-danger-fg">
                Rejected: {row.rejection_reason}
              </div>
            )}
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <UrgencyBadge tier={row.urgency} />
            <StatusChip status={row.status} />
          </div>
        </div>
      </Link>
    </li>
  );
}
